import { gql as contentfulGQL, useQuery } from '@apollo/client'
import { BannerViewModel } from '@/molecules/Banner'
import { getContentfulClient } from '@/services/ApolloClient'
import { GetBannerGroupByIdQuery, UltrawideBanner, UltrawideBannerExperiment } from '@/types/codegen-contentful'
import { buildCloudinaryImagePathFromContentfulObject, Gravity } from '@/utils/Cloudinary'

export const GET_BANNER_GROUP_BY_ID_QUERY = contentfulGQL/* GraphQL */ `
  query getBannerGroupById($id: String!, $locale: String, $preview: Boolean, $region: String) {
    ultrawideBannerGroup(id: $id, locale: $locale, preview: $preview) {
      bannerListCollection(
        limit: 15
        preview: $preview
        locale: $locale
        where: { OR: [{ region_exists: false }, { region_contains_all: [$region] }] }
      ) {
        items {
          ... on UltrawideBanner {
            sys {
              id
            }
            backgroundImage
            tabletBackgroundImage
            mobileBackgroundImage
            altText
            logo {
              image
              title
              height
              width
            }
            description
            href
            guildHref
            logoStyle
            backgroundStyle
            gravity
            region
          }
          ... on UltrawideBannerExperiment {
            experimentId
            experimentBannerListCollection(
              limit: 15
              preview: $preview
              locale: $locale
              where: { OR: [{ region_exists: false }, { region_contains_all: [$region] }] }
            ) {
              items {
                sys {
                  id
                }
                backgroundImage
                tabletBackgroundImage
                mobileBackgroundImage
                altText
                logo {
                  image
                  title
                  height
                  width
                }
                description
                href
                guildHref
                logoStyle
                backgroundStyle
                gravity
                region
                experimentVariation
                defaultExperimentVariation
              }
            }
          }
        }
      }
    }
  }
`

export type GetBannerByIdParams = {
  id: string
  locale: string
  region: string
  preview: boolean
  skip: boolean
}

export const useGetBannerGroupByIdQuery = ({ id, locale, preview, region, skip }: GetBannerByIdParams) => {
  const { data: bannerData, loading: isBannersLoading } = useQuery<GetBannerGroupByIdQuery>(
    GET_BANNER_GROUP_BY_ID_QUERY,
    {
      variables: { id, locale, preview, region },
      skip: !id || skip,
      client: getContentfulClient({ preview }),
    },
  )
  const filteredBanners = filterBannerList(
    bannerData?.ultrawideBannerGroup?.bannerListCollection?.items as (UltrawideBanner | UltrawideBannerExperiment)[],
  )

  return {
    bannerData: filteredBanners as UltrawideBanner[],
    isBannersLoading,
  }
}

export function formatBannerViewModel(ultrawideBanner: UltrawideBanner, isGuildMember: boolean): BannerViewModel {
  const titleImage = ultrawideBanner?.logo
    ? {
        cloudinaryPath: buildCloudinaryImagePathFromContentfulObject(ultrawideBanner.logo.image),
        height: ultrawideBanner.logo.height as number,
        width: ultrawideBanner.logo.width as number,
        alt: ultrawideBanner.altText as string,
      }
    : undefined

  const bannerHref = getBannerHref(ultrawideBanner, isGuildMember)
  return {
    id: ultrawideBanner.sys.id,
    backgroundCloudinaryPath: buildCloudinaryImagePathFromContentfulObject(ultrawideBanner?.backgroundImage),
    tabletBackgroundCloudinaryPath: ultrawideBanner?.tabletBackgroundImage
      ? buildCloudinaryImagePathFromContentfulObject(ultrawideBanner.tabletBackgroundImage)
      : null,
    mobileBackgroundCloudinaryPath: ultrawideBanner?.mobileBackgroundImage
      ? buildCloudinaryImagePathFromContentfulObject(ultrawideBanner.mobileBackgroundImage)
      : null,
    titleImage,
    descriptionText: ultrawideBanner?.description ?? '',
    href: bannerHref,
    backgroundStyle: ultrawideBanner?.backgroundStyle,
    logoStyle: ultrawideBanner?.logoStyle,
    gravity: ultrawideBanner?.gravity as Gravity,
  }

  function getBannerHref(ultrawideBanner: UltrawideBanner, isGuildMember: boolean): string {
    return isGuildMember && !!ultrawideBanner?.guildHref ? ultrawideBanner.guildHref : (ultrawideBanner?.href as string)
  }
}

export function filterBannerList(bannerListCollection: (UltrawideBanner | UltrawideBannerExperiment)[]) {
  return bannerListCollection?.map((item) => {
    if (item?.__typename === 'UltrawideBannerExperiment') {
      const experimentBanner = item.experimentBannerListCollection?.items.filter(
        (i) => i?.defaultExperimentVariation,
      )?.[0]
      return experimentBanner as UltrawideBanner
    } else {
      return item as UltrawideBanner
    }
  })
}
